// SPDX-License-Identifier: MIT
// Dataset size & item/vertex policy guardrails for in-browser Kepler/deck.gl usage.
// Sources: deck.gl performance guidance (1M smooth, ~5M ok, >=10M problematic & memory risk)
// and practical kepler.gl upload UX thresholds.

export interface FilePolicyAssessment {
  allowed: boolean;
  severity?: 'warning' | 'error';
  reason?: string;
  softExceeded?: boolean;
}

export const POLICY = {
  FILE_SIZE_MB: {
    GEOJSON: { SOFT: 100, HARD: 400 },
    NDJSON: { SOFT: 100, HARD: 400 },
    CSV: { SOFT: 200, HARD: 400 },
    DEFAULT_HARD: 400,
  },
  TOTAL_MB: { HARD: 1024 },
  ITEMS: { TARGET: 1_000_000, SOFT: 5_000_000, HARD: 10_000_000 },
} as const;

export function classifyExtension(
  name: string
): 'geojson' | 'ndjson' | 'csv' | 'parquet' | 'json' | 'other' {
  const lower = name.toLowerCase();
  if (lower.endsWith('.geojson')) return 'geojson';
  if (lower.endsWith('.ndjson')) return 'ndjson';
  if (lower.endsWith('.csv')) return 'csv';
  if (
    lower.endsWith('.geoparquet') ||
    lower.endsWith('.parquet') ||
    lower.endsWith('.parq') ||
    lower.endsWith('.pq')
  )
    return 'parquet';
  if (lower.endsWith('.json') || lower.endsWith('.topojson')) return 'json';
  return 'other';
}

export function assessFileSizePolicy(name: string, sizeBytes?: number): FilePolicyAssessment {
  if (sizeBytes == null) return { allowed: true };
  const ext = classifyExtension(name);
  const mb = sizeBytes / (1024 * 1024);
  let soft: number | undefined;
  let hard: number;
  if (ext === 'geojson') {
    soft = POLICY.FILE_SIZE_MB.GEOJSON.SOFT;
    hard = POLICY.FILE_SIZE_MB.GEOJSON.HARD;
  } else if (ext === 'ndjson') {
    soft = POLICY.FILE_SIZE_MB.NDJSON.SOFT;
    hard = POLICY.FILE_SIZE_MB.NDJSON.HARD;
  } else if (ext === 'csv') {
    soft = POLICY.FILE_SIZE_MB.CSV.SOFT;
    hard = POLICY.FILE_SIZE_MB.CSV.HARD;
  } else if (ext === 'parquet') {
    soft = undefined;
    hard = POLICY.FILE_SIZE_MB.DEFAULT_HARD;
  } else if (ext === 'json') {
    soft = POLICY.FILE_SIZE_MB.GEOJSON.SOFT;
    hard = POLICY.FILE_SIZE_MB.GEOJSON.HARD;
  } else {
    soft = undefined;
    hard = POLICY.FILE_SIZE_MB.DEFAULT_HARD;
  }
  if (mb > hard) {
    return { allowed: false, severity: 'error', reason: `File exceeds hard size cap (${hard}MB)` };
  }
  if (soft && mb > soft) {
    return {
      allowed: true,
      severity: 'warning',
      softExceeded: true,
      reason: `File exceeds soft size cap (${soft}MB)`,
    };
  }
  return { allowed: true };
}

export function assessTotalSize(bytesTotal: number): FilePolicyAssessment {
  const hardBytes = POLICY.TOTAL_MB.HARD * 1024 * 1024;
  if (bytesTotal > hardBytes) {
    return {
      allowed: false,
      severity: 'error',
      reason: `Total selection exceeds ${POLICY.TOTAL_MB.HARD}MB hard cap`,
    };
  }
  return { allowed: true };
}

export interface ItemCountAssessment {
  allowed: boolean;
  severity?: 'warning' | 'error';
  reason?: string;
  items?: number;
}

export function assessItemCounts(
  estimatedItems: number,
  runningTotal: number
): ItemCountAssessment {
  const nextTotal = runningTotal + estimatedItems;
  if (estimatedItems >= POLICY.ITEMS.HARD || nextTotal >= POLICY.ITEMS.HARD) {
    return {
      allowed: false,
      severity: 'error',
      reason: `Hard item cap ${POLICY.ITEMS.HARD.toLocaleString()} exceeded`,
      items: estimatedItems,
    };
  }
  if (estimatedItems >= POLICY.ITEMS.SOFT || nextTotal >= POLICY.ITEMS.SOFT) {
    return {
      allowed: true,
      severity: 'warning',
      reason: `High item volume (> ${POLICY.ITEMS.SOFT.toLocaleString()}); performance may degrade`,
      items: estimatedItems,
    };
  }
  return { allowed: true, items: estimatedItems };
}
